import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export default function CustomerRegister() {
  const navigate = useNavigate();
  const { registerCustomer } = useAuth();
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    phone: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }

    setLoading(true);
    try {
      const user = await registerCustomer({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone
      });
      // Best-effort acceptance record — never block registration on this
      try {
        await axios.post(`${API}/legal/accept`, {
          doc_ids: ["terms", "privacy"],
          signer_name: `${formData.first_name} ${formData.last_name}`.trim(),
          signer_email: formData.email,
          signer_role: "customer",
          context: "customer-register",
        });
      } catch (_) { /* non-fatal */ }
      toast.success(`Welcome, ${user.first_name}! Account created.`);
      navigate("/");
    } catch (err) {
      console.error("Registration error:", err);
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-5">
      <Card className="bg-[#121212] border-white/5 max-w-md w-full" data-testid="customer-register-card">
        <CardHeader className="text-center relative">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="absolute left-4 top-4 text-zinc-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-16 h-16 rounded-full bg-[#0047FF]/10 flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-[#4D88FF]" />
          </div>
          <CardTitle className="text-2xl text-white">Create Account</CardTitle>
          <CardDescription className="text-zinc-400">
            Sign up to book rides and deliveries
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="first_name" className="text-zinc-400">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="register-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-zinc-400">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Doe"
                  className="bg-zinc-800 border-white/10 text-white mt-1"
                  data-testid="register-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-400">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="register-email"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-zinc-400">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="555-123-4567"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="register-phone"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-zinc-400">Password *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 characters"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="register-password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-zinc-400">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Confirm your password"
                className="bg-zinc-800 border-white/10 text-white mt-1"
                data-testid="register-confirm-password"
              />
            </div>
            <label className="flex items-start gap-2 pt-1 cursor-pointer" data-testid="customer-accept-terms">
              <Checkbox
                checked={acceptedTerms}
                onCheckedChange={(v) => setAcceptedTerms(!!v)}
                className="mt-0.5"
                data-testid="customer-accept-terms-checkbox"
              />
              <span className="text-xs text-zinc-400 leading-relaxed">
                I agree to the{" "}
                <Link to="/legal/terms" target="_blank" className="text-[#4D88FF] hover:underline">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/legal/privacy" target="_blank" className="text-[#4D88FF] hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-5 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
              data-testid="register-submit"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <p className="text-sm text-zinc-500">
              Already have an account?{" "}
              <Button 
                variant="link" 
                className="text-[#4D88FF] p-0" 
                onClick={() => navigate("/login")}
                data-testid="login-link"
              >
                Sign in
              </Button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
